import React, { useState } from "react";
import { formatDistance } from "date-fns";
import { de } from "date-fns/locale";
import {
	CheckCircle2,
	Clock,
	Eye,
	Loader2,
	RefreshCw,
	XCircle,
} from "lucide-react";
import {
	getPrintJobBlobUrl,
	getPrintJobFileName,
} from "@/dentlab/src/components/print/print-job-file-utils";
import { FileViewerModal } from "@/components/src/file-viewer";
import { Button } from "@/dentlab/src/components/ui/button";
import { TooltipWrapper } from "@/dentlab/src/components/ui/tooltip-wrapper";
import { useDesktopsStore } from "@/dentlab/src/store/Desktops";
import type { PrintJobItem } from "@/dentlab/src/store/Desktops/types";

interface PrintQueueProps {
	organizationId: string;
}

export const PrintQueue: React.FC<PrintQueueProps> = ({ organizationId }) => {
	const printJobs = useDesktopsStore((state) => state.printJobs);
	const fetchPrintJobs = useDesktopsStore((state) => state.fetchPrintJobs);
	const retryPrintJob = useDesktopsStore((state) => state.retryPrintJob);
	const [viewerData, setViewerData] = useState<{
		url: string;
		fileName: string;
	} | null>(null);
	const [refreshing, setRefreshing] = useState(false);

	const handleRefresh = async () => {
		setRefreshing(true);
		await fetchPrintJobs(organizationId);
		setRefreshing(false);
	};

	const handleRetry = async (job: PrintJobItem) => {
		await retryPrintJob(job);
	};

	const handleViewFile = (data: any) => {
		const url = getPrintJobBlobUrl(data);
		if (url) {
			setViewerData({ url, fileName: getPrintJobFileName(data) });
		}
	};

	return (
		<div>
			<div className="flex items-center justify-between mb-3">
				<h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
					Druckaufträge
				</h2>
				<Button
					variant="ghost"
					size="sm"
					onClick={handleRefresh}
					disabled={refreshing}
					aria-label="Druckwarteschlange aktualisieren"
					data-testid="print-queue-refresh"
				>
					<RefreshCw
						className={`h-4 w-4 mr-1 ${
							refreshing ? "animate-spin" : ""
						}`}
					/>
					Aktualisieren
				</Button>
			</div>
			<div
				className="border rounded-lg overflow-hidden"
				data-testid="print-queue-table"
			>
				<table className="w-full">
					<thead>
						<tr className="bg-gray-50 border-b">
							<th className="text-left text-xs font-medium text-gray-500 px-4 py-2.5 w-12">
								Status
							</th>
							<th className="text-left text-xs font-medium text-gray-500 px-4 py-2.5">
								Dokument
							</th>
							<th className="text-left text-xs font-medium text-gray-500 px-4 py-2.5">
								Drucker
							</th>
							<th className="text-left text-xs font-medium text-gray-500 px-4 py-2.5 w-32">
								Zeit
							</th>
							<th className="text-right text-xs font-medium text-gray-500 px-4 py-2.5 w-20"></th>
						</tr>
					</thead>
					<tbody>
						{printJobs.length === 0 && (
							<tr>
								<td
									colSpan={5}
									className="px-4 py-8 text-center text-sm text-gray-400"
								>
									Keine Druckaufträge
								</td>
							</tr>
						)}
						{printJobs.map((item) => (
							<tr
								key={item.id}
								className="group border-b last:border-b-0 hover:bg-gray-50"
							>
								<td className="px-4 py-2">
									{item.status === "failed" ? (
										<TooltipWrapper
											title={
												item.response_message ??
												"Fehler"
											}
										>
											<XCircle className="h-4 w-4 text-red-500" />
										</TooltipWrapper>
									) : item.status === "processing" ? (
										<Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
									) : item.status === "completed" ? (
										<TooltipWrapper title="An Drucker gesendet">
											<CheckCircle2 className="h-4 w-4 text-green-500" />
										</TooltipWrapper>
									) : (
										<Clock className="h-4 w-4 text-amber-500" />
									)}
								</td>
								<td className="px-4 py-2 text-sm">
									<button
										onClick={() =>
											handleViewFile(item.data)
										}
										className="inline-flex items-center gap-1 hover:text-blue-600 transition-colors"
										aria-label="Dokument anzeigen"
										data-testid={`print-queue-view-file-${item.id}`}
									>
										{getPrintJobFileName(item.data)}
										<Eye className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
									</button>
								</td>
								<td className="px-4 py-2 text-sm text-gray-600">
									{item.printer_name}
								</td>
								<td className="px-4 py-2 text-xs text-gray-400">
									{formatDistance(
										new Date(item.created_at),
										new Date(),
										{
											addSuffix: true,
											locale: de,
										}
									)}
								</td>
								<td className="px-4 py-2 text-right">
									{item.status === "failed" && (
										<Button
											variant="ghost"
											size="sm"
											onClick={() => handleRetry(item)}
											aria-label="Druckauftrag wiederholen"
											data-testid={`print-queue-retry-${item.id}`}
											className="h-7"
										>
											<RefreshCw className="h-3 w-3" />
										</Button>
									)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{viewerData && (
				<FileViewerModal
					fileIds={[]}
					isOpen={true}
					onClose={() => setViewerData(null)}
					blobUrl={viewerData.url}
					blobFileName={viewerData.fileName}
				/>
			)}
		</div>
	);
};
